/**
 * 801 고소작업대 안전 점검표 — AI 음성 작성용 의미(semantic) 스키마
 *
 * Write.vue 의 폼 키(inputA_1, inspectList[3].isChecked ...)는 사람이 읽어도
 * 의미를 알 수 없으므로 LLM 에 그대로 노출하지 않는다. 이 파일이 그 사이를 잇는다.
 *
 *   폼 경로(물리)        inputA_1 / inspectList[3].isChecked
 *   의미 ID(논리)        inspect_date / chk_overload_device      ← LLM 이 읽고 쓰는 유일한 키
 *   라벨·별칭            "점검일" / ["점검한 날", "오늘 점검"]     ← 프롬프트에만 사용
 *
 * 스키마에 없는 의미 ID 는 클라이언트/서버 양쪽에서 버린다(환각·주입 방어).
 *
 * 새 게시판에 확장할 때는 해당 Write.vue 와 같은 폴더에 ai-voice-schema.js 를
 * 추가하기만 하면 된다(include.js 가 require.context 로 자동 수집).
 * 스키마 계약은 src/util/AiVoiceForm.js 상단 주석 참고.
 */
import inspectData from "./inspectData";

// 점검표 각 행의 의미 ID / 매칭 키워드. index 는 inspectList 배열 위치(고정).
const CHECK_ITEMS = [
  { id: "chk_safety_certification", index: 0, keywords: ["안전인증", "안전검사", "수검"] },
  { id: "chk_ppe_harness", index: 1, keywords: ["안전모", "안전대", "개인보호구", "추락방지", "보호구"] },
  { id: "chk_guardrail", index: 2, keywords: ["안전난간", "난간"] },
  { id: "chk_overload_device", index: 3, keywords: ["과부하방지장치", "과부하", "정격하중"] },
  { id: "chk_emergency_stop", index: 4, keywords: ["비상정지장치", "비상정지"] },

  { id: "chk_boom_angle_sensor", index: 5, keywords: ["각도센서", "붐길이", "센서"] },
  { id: "chk_boom_load_radius", index: 6, keywords: ["허용작업반경", "작업반경", "하중", "반경"] },
  { id: "chk_outrigger_interlock", index: 7, keywords: ["아웃트리거", "인터록", "아웃리거"] },
  { id: "chk_boom_weld_turntable", index: 8, keywords: ["용접부", "균열", "턴테이블", "볼트"] },
  { id: "chk_wire_rope_chain", index: 9, keywords: ["와이어로프", "와이어", "체인", "마모"] },

  { id: "chk_overrise_device", index: 10, keywords: ["과상승방지장치", "과상승"] },
  { id: "chk_alarm_device", index: 11, keywords: ["경보장치", "경보음", "경보"] },
  { id: "chk_control_interlock", index: 12, keywords: ["제어장치", "연동구조", "우발동작"] },
  { id: "chk_foot_switch", index: 13, keywords: ["풋스위치", "발판스위치", "미끄럼방지"] },
  { id: "chk_drop_prevention_valve", index: 14, keywords: ["낙하방지밸브", "유압", "밸브"] },
];

export default {
  boardId: 801,
  title: "고소작업대 안전 점검표",

  // 프롬프트에 덧붙일 도메인 힌트 (선택)
  notes: [
    "고소작업대(차량탑재형/시저형) 현장 점검 결과를 받아 적는 상황이다.",
    "점검 결과 표현: '이상 없다/괜찮다/정상' → 양호, '불량/미비/안 됨/문제 있음' → 미흡, '해당 없음/없는 장비/적용 안 됨' → 해당없음.",
  ],

  fields: [
    {
      id: "inspect_date",
      path: "inputA_1",
      label: "점검일",
      type: "date",
      required: true,
      aliases: ["점검일자", "점검한 날", "점검 날짜"],
    },
    {
      id: "org_name",
      path: "inputA_2",
      label: "소속명",
      type: "text",
      required: true,
      aliases: ["소속", "업체", "회사", "협력업체"],
      // 로그인한 작성자의 회사명으로 자동 채움(발화에 다른 소속이 없을 때)
      speakerKey: "company",
    },
    {
      id: "inspector",
      path: "inputA_3",
      label: "점검자",
      type: "text",
      required: true,
      aliases: ["점검한 사람", "검사자", "확인자"],
      // 대부분 작성자가 곧 점검자다
      speakerKey: "name",
    },
  ],

  checklists: [
    {
      id: "inspect_list",
      path: "inspectList",
      label: "점검항목",
      valueEnum: ["양호", "미흡", "해당없음"],
      noteKey: "비고",
      required: true,
      items: CHECK_ITEMS.map((item) => ({
        ...item,
        group: inspectData[item.index].title,
        label: inspectData[item.index].content.split("\n")[0],
      })),
    },
  ],

  // AI 가 건드리지 않는 영역(진행률 분모에서도 제외)
  ignored: ["stdInfo", "sign1", "sign2", "sign3", "sign4", "photoList", "fileList"],

  examples: [
    "오늘 점검했고, 소속은 대한건설, 점검자는 김철수입니다.",
    "안전난간이랑 비상정지장치는 양호하고, 과부하방지장치는 미흡합니다.",
    "차량탑재형 항목은 전부 해당없음이고, 시저형 과상승방지장치는 양호합니다.",
    "풋스위치 미끄럼방지 표면이 닳아 있어서 미흡, 비고에 교체 예정이라고 적어주세요.",
  ],
};
